import { useDrawer } from "@/context/useDrawer";
import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { EditorPromptState } from "@/context/PromptEditorContext";
import { ChatMessageData } from "@/services/promptEditor.service";
import Button, { ButtonProps } from "@components/library/Button";
import { ContextMenu } from "@components/library/ContextMenu";
import IconButton, { IconButtonProps } from "@components/library/IconButton";
import { CommandIcon, LoadingIcon, PlayIcon, RefreshIcon } from "@components/library/Icons";
import Tooltip from "@components/library/Tooltip";
import { ToolName } from "@components/llama/VariableName";
import { InputTag } from "@components/PromptEditor/VariableTags";
import { InlineMenu } from "@components/SimpleTable/InlineMenu";
import { Disclosure } from "@headlessui/react";
import { DuplicateIcon, FlagIcon, MinusCircleIcon, PlusIcon } from "@heroicons/react/outline";
import { produce } from "immer";
import { classNames, generateId } from "lib/utils";
import React, { HTMLProps } from "react";
import { isMacOs } from "react-device-detect";
import ScrollToBottom from "react-scroll-to-bottom";
import { SSE } from "sse.js";
import { ToolResult } from "services/v4.service";
import { ContentRow } from "./ContentRow";
import { extractInputsFromEditorPromptState, extractToolsFromPrompt } from "./utils";
import { ArrowsOutSimple } from "@phosphor-icons/react";

export type DatumStatus = "not_run" | "loading" | "suspended" | "complete" | "stale" | "error" | "cancelled";

type BaseDatum = {
  id: string;
  inputs: Record<string, string>;
  status: DatumStatus;
  streamSource: SSE | null;
  error: string | null;
  tool_results: ToolResult[] | null;
};

export type CompletionDatum = BaseDatum & {
  type: "completion";
  logId?: string; // If this is returned from the API, we'll be able to show the full Log object.
  output: string | null;
  raw_output: string | null;
};

export type ChatDatum = BaseDatum & {
  logId?: string | null; // If this is returned from the API, we can continue the log.
  type: "chat";
  /** The messages that user defined before sending the request to the model or agent */
  messages: ChatMessageData[];
  /** The messages that the model or agent returned */
  outputMessages: ChatMessageData[];
  /** The messages appended by the user to before sending a continue request */
  continueMessages: ChatMessageData[];
  /** Created before any logs to tie them together */
  session_reference_id: string;
  /** On response, we'll get the session_id, and we use this to be able to link to the session drawer */
  session_id?: string;
};

export type Datum = CompletionDatum | ChatDatum;

export const createCompletionDatum = (inputs?: Record<string, string>): CompletionDatum => ({
  type: "completion",
  id: generateId("datum"),
  inputs: inputs || {},
  status: "not_run",
  output: null,
  raw_output: null,
  streamSource: null,
  error: null,
  tool_results: null,
});

export const createChatDatum = (inputs?: Record<string, string>, messages?: ChatDatum["messages"]): ChatDatum => ({
  type: "chat",
  id: generateId("datum"),
  inputs: inputs || {},
  status: "not_run",
  outputMessages: [],
  continueMessages: [],
  messages: messages || [
    {
      _id: generateId("message"),
      _status: "not_loading",
      role: "user",
      content: "",
      thinking: [],
    },
  ],
  streamSource: null,
  error: null,
  tool_results: null,
  session_reference_id: generateId("editor_session"),
});

interface PlaygroundDataProps {
  inputs: string[]; // Names of the inputs
  tools: { name: string; call: string }[]; // Names and the call signature of the tools
  datums: Datum[];
  setDatums: (data: Datum[]) => void;
  prompt: string;
  onRun: (id: string) => void;
  onCancel: (id: string) => void;
  onRunAll: () => void;
  cancelAll: () => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  preview?: { editorPromptState: EditorPromptState; datums: CompletionDatum[] };
}

// This element is completion-only.
const PlaygroundData = ({
  inputs,
  tools,
  datums,
  setDatums,
  prompt,
  onRun,
  onCancel,
  onRunAll,
  cancelAll,
  onDuplicate,
  onDelete,
  preview,
}: PlaygroundDataProps) => {
  let displayedInputs = inputs;
  let displayedTools = tools;
  let displayedDatums = datums;

  const previewMode = preview !== undefined;
  if (previewMode) {
    displayedInputs = extractInputsFromEditorPromptState(preview.editorPromptState);
    displayedTools = preview.editorPromptState.prompt_template
      ? extractToolsFromPrompt(preview.editorPromptState.prompt_template)
      : [];
    displayedDatums = preview.datums;
  }

  return (
    <div className="flex flex-col">
      {displayedDatums
        .filter((datum) => datum.type === "completion")
        .map((datum, i) => {
          // Appease typescript
          if (datum.type !== "completion") return null;
          return (
            <DatumCard
              shade={i % 2 === 0 ? "dark" : "light"}
              key={datum.id}
              inputs={displayedInputs}
              tools={displayedTools}
              datum={datum}
              prompt={prompt}
              setInputValue={(input, value) => {
                setDatums(
                  produce(datums, (draft) => {
                    const target = draft.find((d) => d.id === datum.id);
                    if (target === undefined) {
                      console.error("Could not find datum with id", datum.id);
                      return;
                    }
                    target["inputs"][input] = value;
                    target.status = "stale";
                  }),
                );
              }}
              onRun={() => onRun(datum.id)}
              onCancel={() => onCancel(datum.id)}
              onDuplicate={() => onDuplicate(datum.id)}
              onDelete={() => onDelete(datum.id)}
              previewMode={previewMode}
            />
          );
        })}
      {!previewMode && (
        <div className="mx-auto mt-32 flex w-full max-w-screen-lg gap-12 px-24">
          <Button
            shade={"gray"}
            size={24}
            IconLeft={PlusIcon}
            onClick={() => setDatums([...datums, createCompletionDatum()])}
          >
            Completion
          </Button>
          {datums.some((datum) => datum.status === "loading") ? (
            <Button IconLeft={LoadingIcon} size={24} onClick={cancelAll} shade="blue">
              Cancel all
            </Button>
          ) : (
            <Button IconLeft={PlayIcon} onClick={onRunAll} size={24} disabled={previewMode} shade="blue">
              Run all
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlaygroundData;

interface DatumCardProps {
  inputs: string[];
  tools: { name: string; call: string }[];
  datum: CompletionDatum;
  prompt: string;
  setInputValue: (input: string, value: string) => void;
  onRun: () => void;
  onCancel: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  previewMode?: boolean;
  shade?: "light" | "dark";
}

const DatumCard = ({
  inputs,
  tools,
  datum,
  prompt,
  setInputValue,
  onRun,
  onCancel,
  onDuplicate,
  onDelete,
  previewMode,
  shade,
}: DatumCardProps) => {
  const [viewState, setViewState] = React.useState<"open" | "closed">("open"); // Named to mimic radix-ui behaviour

  // Add a ref to show when component has focus
  const ref = React.useRef<HTMLDivElement>(null);

  const hasPrompt = prompt !== "";
  const hasInputs = inputs.length > 0;
  const hasOutput = datum.output !== null;

  const controls = (
    <DatumControls
      datum={datum}
      onDuplicate={onDuplicate}
      onDelete={() => {
        setViewState("closed");
        onCancel();
        setTimeout(() => {
          onDelete();
        }, 300);
      }}
      disabled={previewMode}
    />
  );
  return (
    <div
      ref={ref}
      key={datum.id}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.metaKey && e.key === "Enter" && datum.status !== "loading") {
          onRun();
        } else if (datum.status === "loading" && e.key === "Escape") {
          onCancel();
        }
      }}
      className={classNames(
        // TODO: shade should be gray-50 but hard to dev (edit: i'm now used to it too)
        shade === "dark" ? "bg-oldgray-100" : "bg-white",
        "group relative flex flex-col overflow-clip border-b border-oldgray-200 py-16 transition-all first:border-t",
        !previewMode && (viewState === "closed" ? "animate-fade-out-up" : "animate-fade-in-down"),
      )}
    >
      <main className="relative mx-auto grid w-full max-w-screen-lg px-24">
        <div className="absolute right-24 z-[1]">{controls}</div>

        {inputs.map((input) => {
          const inputValue = datum.inputs[input];
          return (
            <ContentRow
              key={input}
              role={
                <InputTag className="" size={20}>
                  {input}
                </InputTag>
              }
            >
              <DatumInput
                value={inputValue}
                setValue={(value) => setInputValue(input, value)}
                error={datum.status === "error" && !(input in datum.inputs)}
                disabled={previewMode}
              />
            </ContentRow>
          );
        })}
        <ContentRow
          role={
            <Tooltip
              side="top"
              content={
                <span className="flex flex-wrap items-center">
                  {datum.status === "loading" ? (
                    "Click to cancel"
                  ) : (
                    <>Run {isMacOs ? <CommandIcon className="mx-4 ml-8 inline-block h-12 w-12" /> : "Ctrl"} + Enter</>
                  )}
                </span>
              }
            >
              <DatumRunButton
                status={datum.status}
                onRun={onRun}
                onCancel={onCancel}
                disabled={!hasPrompt || previewMode}
              />
            </Tooltip>
          }
        >
          <ScrollToBottom
            initialScrollBehavior="auto"
            mode={previewMode ? "top" : "bottom"}
            className={classNames(
              hasInputs && "border-t border-gray-150",
              "max-h-480 w-full overflow-y-auto break-words pl-4 text-14-20",
              datum.status === "stale" && "opacity-60",
            )}
          >
            {tools.length > 0 && (
              <section className={classNames("flex flex-col gap-8", tools.length > 0 && "py-6 ")}>
                {/* TODO: Make robust. */}
                {tools?.map((tool) => {
                  const toolResult =
                    datum.tool_results?.find((t) => t.signature.split("(")[0] === tool.name) ||
                    ({
                      name: tool.name,
                      result: "",
                      signature: tool.call,
                    } as ToolResult);
                  const runAndEmpty = datum.status !== "not_run" && toolResult?.result === "";
                  return <DatumToolResult key={tool.name} toolResult={toolResult} runAndEmpty={runAndEmpty} />;
                })}
              </section>
            )}

            <div className="py-8 pr-[8px]">
              {datum.status === "error" && datum.error && (
                <div className="flex gap-8 font-sans text-red-800">
                  <FlagIcon className="mt-2 h-16 w-16 shrink-0 text-red-700" /> {datum.error}
                </div>
              )}
              {datum.status === "not_run" &&
                (datum.output === "" || datum.output === null) &&
                (!hasPrompt ? (
                  <div className="flex gap-8 font-sans text-gray-400">Add a prompt template to run</div>
                ) : (
                  <div className="flex select-none gap-8 font-sans text-gray-300">Run to generate completion</div>
                ))}
              {datum.status === "complete" && datum.output === "" ? (
                <div className="flex gap-8 font-sans text-gray-600">
                  <FlagIcon className="mt-2 h-16 w-16 shrink-0" /> Response contained no text. Consider adjusting your
                  prompt or stop sequences.
                </div>
              ) : (
                <span className="whitespace-pre-wrap break-words bg-oldgreen-200 ">{datum.output?.trim()}</span>
              )}
            </div>
          </ScrollToBottom>
        </ContentRow>
      </main>
    </div>
  );
};

export const ChatInputs = ({
  inputs,
  datum,
  disabled,
  setInputValue,
}: {
  inputs: string[];
  datum: Datum;
  disabled: boolean;
  setInputValue: (input: string, value: string) => void;
}) => {
  return (
    <div className="mx-auto max-w-screen-lg grow">
      {inputs.map((input) => {
        const inputValue = datum.inputs[input];
        return (
          <ContentRow key={input} role={<InputTag size={20}>{input}</InputTag>}>
            <DatumInput
              value={inputValue}
              setValue={(value) => setInputValue(input, value)}
              error={datum.status === "error" && !(input in datum.inputs)}
              disabled={disabled}
            />
          </ContentRow>
        );
      })}
    </div>
  );
};

interface DatumInputProps {
  value?: string;
  setValue: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

export const DatumInput = ({
  value,
  setValue,
  error,
  disabled,
  className,
  ...props
}: DatumInputProps & HTMLProps<HTMLTextAreaElement>) => {
  // Crudely make it obvious that there is a multiline input, cast it as a string as it might be treated as number
  const rows = (value && value.toString().includes("\n")) || (value?.length && value?.length > 60) ? 5 : 1;

  return (
    <div className="relative">
      <textarea
        className={classNames(
          "mt-2 min-h-[34px] w-full rounded-ms bg-transparent text-oldgray-800 ",
          "px-6 py-4 text-14-24",
          // Not sure how to best indicate to the user there is a value here (in case they don't see a new line etc).
          // This works ish for now.
          "border-transparent hover:bg-oldgray-100 focus:border-transparent focus:bg-oldgray-200 focus:outline-none focus:ring-0 active:bg-oldgray-100",
          error && "!border-stroke-danger-1 ",
          value === undefined && "font-mono",
          PRIVACY_CLASSNAMES["moderate"],
          className,
        )}
        rows={rows}
        // If the value is null, show the placeholder because the API will error if not set.
        // If it is empty string, that's ok, we'll send empty string, so no placeholder.
        // Also, if value becomes null/undefined, this textarea becomes uncontrolled and doesn't
        // reset so we still pass in empty string as the value.
        placeholder={value === undefined ? "none" : ""}
        value={value || ""}
        onChange={(event) => setValue(event.target.value)}
        disabled={disabled}
        {...props}
      />
    </div>
  );
};

interface ToolResultProps {
  value?: string;
  toolResult: ToolResult;
  runAndEmpty: boolean;
}

const DatumToolResult = ({ toolResult, runAndEmpty }: ToolResultProps) => {
  const hasResult = toolResult.result !== "";
  return (
    <Disclosure defaultOpen>
      <Disclosure.Button
        className={classNames(
          "max-w-[200px] rounded-ms py-4 hover:bg-oldgray-200 focus:outline-none focus-visible:outline",
          hasResult ? " text-oldpurple-700 opacity-100" : "text-gray cursor-default opacity-50",
        )}
      >
        <ToolName name={toolResult.name} />
      </Disclosure.Button>

      <Disclosure.Panel className="-my-4 max-h-240 w-full min-w-0 grow overflow-y-auto text-14-20">
        <div className={classNames("max-h-160 w-full whitespace-pre-wrap break-words rounded-ms text-14-20")}>
          {/* TODO: handle empty / not run yet etc. */}
          <span className=" bg-oldpurple-300/70">{toolResult?.result}</span>
        </div>
      </Disclosure.Panel>
    </Disclosure>
  );
};

const DatumControls = ({
  datum,
  onDuplicate,
  onDelete,
  disabled,
}: {
  datum: CompletionDatum;
  onDuplicate: () => void;
  onDelete: () => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={classNames("flex items-start gap-2 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100")}
    >
      <IconButton tooltip="Delete" Icon={MinusCircleIcon} shade={"black"} onClick={onDelete} disabled={disabled} />
      <ShowLogDrawerIconButton shade="black" logId={datum.logId} />
      <InlineMenu className="" size={24} shade="black">
        <ContextMenu.Item
          withinDropdownMenu
          key="duplicate"
          IconLeft={DuplicateIcon}
          disabled={disabled}
          onClick={(event) => {
            onDuplicate();
          }}
        >
          Duplicate
        </ContextMenu.Item>
      </InlineMenu>
    </div>
  );
};

export const DatumRunButton = ({
  status,
  onRun,
  onCancel,
  disabled,
}: {
  status: DatumStatus;
  onRun: () => void;
  onCancel: () => void;
  disabled?: boolean;
}) => {
  return (
    <div className="flex ">
      <span>
        {status === "loading" ? (
          <Button
            className="w-full bg-white"
            IconLeft={LoadingIcon}
            styling="outline"
            size={24}
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </Button>
        ) : status === "complete" ? (
          <Button
            className="w-full bg-white"
            IconLeft={RefreshIcon}
            styling="outline"
            size={24}
            onClick={onRun}
            disabled={disabled}
          >
            Re-run
          </Button>
        ) : (
          <Button
            className="w-full bg-white"
            IconLeft={PlayIcon}
            styling="outline"
            size={24}
            onClick={onRun}
            disabled={disabled}
          >
            Run
          </Button>
        )}
      </span>
    </div>
  );
};

export const ShowLogDrawerIconButton = ({ logId, ...props }: { logId?: string } & Omit<IconButtonProps, "Icon">) => {
  const { setOpen: setDrawerOpen, setLogId } = useDrawer();

  return (
    <IconButton
      tooltip={!logId ? "No associated Log" : "View Log"}
      Icon={ArrowsOutSimple}
      size={24}
      disabled={!logId}
      shade="black"
      onClick={(event) => {
        setLogId(logId);
        setDrawerOpen(true);
      }}
      {...props}
    />
  );
};

export const ShowLogDrawerButton = ({
  logId,
  // content = "View Log",
  ...props
}: { logId?: string } & Omit<ButtonProps, "IconLeft" | "IconRight">) => {
  const { setOpen: setDrawerOpen, setLogId } = useDrawer();

  return (
    <Tooltip content={!logId ? "No associated Log" : "View Log"}>
      <Button
        IconLeft={ArrowsOutSimple}
        size={20}
        disabled={!logId}
        onClick={(event) => {
          setLogId(logId);
          setDrawerOpen(true);
        }}
        {...props}
      />
    </Tooltip>
  );
};
